import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Tag } from 'primereact/tag';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import Layout from '../component/layout';
import { toastify } from '../../../utils/toast';
import { getItem } from '../../../utils/localStorage';
import { makeRequest2 } from '../../../utils/makeRequest';
import { getCustomersURL, resendLoginCredentials, importKraPinsURL } from '../../../utils/urls';
import { filterObjectsByAllProperties } from '../../../utils/filterSearch';

function CustomerManagement() {
  const navigate = useNavigate();
  const kraFileInputRef = useRef(null);

  const [customers, setCustomers] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [resendDialogVisible, setResendDialogVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);

  // KRA Import state
  const [kraImportDialogVisible, setKraImportDialogVisible] = useState(false);
  const [kraImportLoading, setKraImportLoading] = useState(false);
  const [kraImportResults, setKraImportResults] = useState(null);

  const fetchCustomersForFacility = async () => {
    try {
      const value = await getItem('selectedFacilityId');
      const response = await makeRequest2(`${getCustomersURL}/${value}`, 'GET', {});
      if (response.success) {
        const sortedCustomers = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setAllCustomers(sortedCustomers);
        setCustomers(sortedCustomers);
      } else {
        throw new Error('Failed to fetch customers');
      }
    } catch (error) {
      toastify(error.message, 'error');
    }
  };

  const exportCustomersToCSV = () => {
    if (!customers || customers.length === 0) {
      toastify('No customers to export', 'error');
      return;
    }
    const headers = ['Full Name', 'Email', 'Phone Number', 'ID Number', 'Customer Type', 'Resident Type'];
    const rows = customers.map((customer) => [
      `${customer.firstName || ''} ${customer.lastName || ''}`,
      customer.email || '',
      customer.phoneNumber || '',
      customer.idNumber || '',
      customer.customerType || '',
      customer.residentType || '',
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((value) => `"${value}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `customers_export_${new Date().toISOString()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // --- KRA PIN Import ---
  const handleKraFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // reset so same file can be re-selected
    if (!file) return;

    const allowed = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    if (!allowed.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
      toastify('Please upload a valid Excel file (.xlsx or .xls)', 'error');
      return;
    }

    setKraImportResults(null);
    setKraImportLoading(true);
    setKraImportDialogVisible(true);

    try {
      const facilityId = await getItem('selectedFacilityId');
      const formData = new FormData();
      formData.append('file', file);

      // makeRequest2 detects FormData and omits Content-Type so the browser sets the multipart boundary
      const response = await makeRequest2(`${importKraPinsURL}/${facilityId}`, 'POST', formData);

      if (response.success) {
        setKraImportResults(response.data.data);
        toastify(response.data.message, 'success');
        fetchCustomersForFacility();
      } else {
        toastify(response.error || 'Import failed', 'error');
        setKraImportDialogVisible(false);
      }
    } catch (error) {
      console.error('KRA import error:', error);
      toastify('An error occurred during import', 'error');
      setKraImportDialogVisible(false);
    } finally {
      setKraImportLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      updated: { bg: '#d4edda', color: '#155724', label: 'Updated' },
      not_found: { bg: '#fff3cd', color: '#856404', label: 'Not Found' },
      skipped: { bg: '#e2e3e5', color: '#383d41', label: 'Skipped' },
      error: { bg: '#f8d7da', color: '#721c24', label: 'Error' },
    };
    const style = map[status] || map.skipped;
    return (
      <span
        style={{
          backgroundColor: style.bg,
          color: style.color,
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 600,
        }}
      >
        {style.label}
      </span>
    );
  };

  // --- Resend credentials ---
  const handleResendCredentials = (customer) => {
    setSelectedCustomer(customer);
    setResendDialogVisible(true);
  };

  const confirmResend = async () => {
    setResendLoading(true);
    try {
      const customerId = selectedCustomer._id;
      const facilityId = await getItem('selectedFacilityId');
      const response = await makeRequest2(`${resendLoginCredentials}/${customerId}/${facilityId}`, 'POST', {});
      if (response.success) {
        toastify(`Login credentials resent to ${selectedCustomer.firstName}`, 'success');
        setResendDialogVisible(false);
        setSelectedCustomer(null);
      } else {
        toastify(response.error, 'error');
      }
    } catch (error) {
      console.error('Error resending credentials:', error);
    } finally {
      setResendLoading(false);
    }
  };

  const toTitleCase = (str) => {
    if (!str) return '';
    return str
      .split(/(\s+)/)
      .map((part) => {
        if (part.trim() === '') return part;
        if (part.includes('-'))
          return part.split('-').map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join('-');
        if (part.includes("'"))
          return part.split("'").map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join("'");
        if (part.toLowerCase().startsWith('mc') && part.length > 2)
          return 'Mc' + part.charAt(2).toUpperCase() + part.slice(3).toLowerCase();
        if (part.toLowerCase().startsWith('mac') && part.length > 3)
          return 'Mac' + part.charAt(3).toUpperCase() + part.slice(4).toLowerCase();
        const lowercasePrefixes = ['de', 'da', 'del', 'della', 'van', 'von', 'el', 'la', 'le'];
        if (lowercasePrefixes.includes(part.toLowerCase())) return part.toLowerCase();
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      })
      .join('');
  };

  const handleInputSearch = async (value) => {
    try {
      setSearchTerm(value);
      if (value === '') {
        setCustomers(allCustomers);
      } else {
        const results = await filterObjectsByAllProperties(allCustomers, value);
        setCustomers(results);
      }
    } catch (error) {
      toastify(error.message, 'error');
    }
  };

  const HandeFilter = async (typeOrStatus) => {
    try {
      if (typeOrStatus === 'All') {
        setCustomers(allCustomers);
      } else {
        const results = allCustomers.filter(
          (x) => x.customerType === typeOrStatus || x.status === typeOrStatus || x.residentType === typeOrStatus
        );
        setCustomers(results);
      }
    } catch (error) {
      toastify(error.message, 'error');
    }
  };

  const handleCustomerClick = (rowData) => {
    const name = rowData.fullName
      ? toTitleCase(rowData.fullName)
      : `${toTitleCase(rowData.firstName)} ${toTitleCase(rowData.lastName)}`;

    const fullName = rowData.salutation
      ? `${toTitleCase(rowData.salutation)} ${name}`
      : name;

    return (
      <Link
        to={'/facility/customer_management/view_customer/' + rowData._id}
        style={{ color: 'royalblue' }}
      >
        {fullName}
      </Link>
    );
  };

  const residentTypeTemplate = (rowData) => {
    const residentType = toTitleCase(rowData.residentType);
    const usage = rowData.nonResidentUsage ? ` (${toTitleCase(rowData.nonResidentUsage)})` : '';
    return `${residentType}${usage}`;
  };

  const customerKindTemplate = (rowData) => (
    <span
      className={`badge rounded-pill ${rowData.customerKind === "Business"
          ? "bg-primary-subtle text-primary"
          : "bg-success-subtle text-success"
        }`}
      style={{
        fontSize: "0.75rem",
        fontWeight: 600,
        padding: "0.4rem 0.7rem",
      }}
    >
      <i
        className={`ti ${rowData.customerKind === "Business"
            ? "ti-building-store"
            : "ti-user"
          } me-1`}
      ></i>
      {rowData.customerKind}
    </span>
  );

  const phoneNumberTemplate = (rowData) => '+254' + rowData.phoneNumber;

  useEffect(() => {
    fetchCustomersForFacility();
  }, []);

  return (
    <Layout>
      {/* Hidden file input for KRA import */}
      <input
        type="file"
        ref={kraFileInputRef}
        style={{ display: 'none' }}
        accept=".xlsx,.xls"
        onChange={handleKraFileChange}
      />

      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to={'/facility/'}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={'#'}>Customer Management</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-header">
                  <h5>Customers</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-lg-12 col-xxl-12">
                      <div className="card">
                        <div className="card-body position-relative">
                          <div className="row d-flex align-items-center gap-3">
                            {/* Search */}
                            <div className="col-md-3 col-sm-6">
                              <input
                                className="form-control"
                                placeholder="Search here"
                                value={searchTerm}
                                onChange={(e) => handleInputSearch(e.target.value)}
                              />
                            </div>

                            {/* Filter */}
                            <div className="col-md-2 col-sm-4">
                              <div className="btn-group-dropdown">
                                <button
                                  className="btn btn-outline-default dropdown-toggle"
                                  type="button"
                                  data-bs-toggle="dropdown"
                                  aria-haspopup="true"
                                  aria-expanded="false"
                                >
                                  <i className="ti ti-filter"></i>
                                </button>
                                <div className="dropdown-menu">
                                  <Link className="dropdown-item" to="#!" onClick={() => HandeFilter('All')}>All</Link>
                                  <Link className="dropdown-item" to="#!" onClick={() => HandeFilter('home owner')}>Home Owners</Link>
                                  <Link className="dropdown-item" to="#!" onClick={() => HandeFilter('tenant')}>Tenants</Link>
                                  <Link className="dropdown-item" to="#!" onClick={() => HandeFilter('resident')}>Residents</Link>
                                  <Link className="dropdown-item" to="#!" onClick={() => HandeFilter('non-resident')}>Non-Residents</Link>
                                  <Link className="dropdown-item" to="#!" onClick={() => HandeFilter('Active')}>Active</Link>
                                  <Link className="dropdown-item" to="#!" onClick={() => HandeFilter('Inactive')}>Inactive</Link>
                                </div>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="col-md-6 col-sm-12 text-md-end d-flex gap-2 justify-content-md-end flex-wrap">
                              <button
                                className="btn btn-primary"
                                onClick={() => navigate('/facility/customer_management/add_new_customer')}
                              >
                                Add New Customer
                              </button>

                              <button className="btn btn-success" onClick={exportCustomersToCSV}>
                                Export Customers
                              </button>

                              <button
                                className="btn btn-warning"
                                onClick={() => kraFileInputRef.current && kraFileInputRef.current.click()}
                                title="Import KRA PINs from Excel (.xlsx). Required columns: Email, KRA PIN"
                              >
                                <i className="ti ti-file-import me-1"></i>
                                Import KRA PINs
                              </button>
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-md-12">
                              <div className="dt-responsive table-responsive">
                                <DataTable
                                  value={customers}
                                  emptyMessage="No customers found."
                                  sortMode="multiple"
                                  paginator
                                  rows={10}
                                  stripedRows
                                  tableStyle={{ minWidth: '50rem' }}
                                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                  currentPageReportTemplate="Showing {first} to {last} of {totalRecords} customers"
                                  rowsPerPageOptions={[10, 15, 25, 50, 75, 100]}
                                >
                                  <Column field="customerKind" header="Type" body={customerKindTemplate} />
                                  <Column header="Full Name" body={handleCustomerClick} />
                                  <Column
                                    field="email"
                                    header="Email"
                                    body={(rowData) => (
                                      <a href={`mailto:${rowData.email}`} style={{ color: '#0066cc', textDecoration: 'none' }}>
                                        {rowData.email}
                                      </a>
                                    )}
                                  />
                                  <Column header="Phone" body={phoneNumberTemplate} />
                                  <Column header="Customer Type" body={(rowData) => toTitleCase(rowData.customerType)} />
                                  <Column header="Resident Type" body={residentTypeTemplate} />
                                  <Column
                                    header="KRA PIN"
                                    body={(rowData) =>
                                      rowData.kraPin ? (
                                        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{rowData.kraPin}</span>
                                      ) : (
                                        <span style={{ color: '#aaa', fontSize: '12px' }}>—</span>
                                      )
                                    }
                                  />
                                  <Column
                                    header="Status"
                                    body={(rowData) => (
                                      <Tag severity={rowData.status === 'Inactive' ? 'danger' : 'success'} value={rowData.status} />
                                    )}
                                  />
                                  <Column
                                    header="Actions"
                                    body={(rowData) => (
                                      <Button
                                        icon="pi pi-send"
                                        className="p-button-rounded p-button-text p-button-sm"
                                        tooltip="Resend Login Credentials"
                                        tooltipOptions={{ position: 'top' }}
                                        onClick={() => handleResendCredentials(rowData)}
                                      />
                                    )}
                                    style={{ width: '80px' }}
                                  />
                                </DataTable>

                                {/* Resend Credentials Dialog */}
                                <Dialog
                                  header="Resend Login Credentials"
                                  visible={resendDialogVisible}
                                  style={{ width: '450px' }}
                                  modal
                                  className="p-fluid"
                                  footer={
                                    <div className="flex justify-content-end gap-2">
                                      <Button
                                        label="Cancel"
                                        icon="pi pi-times"
                                        className="p-button-text"
                                        onClick={() => { setResendDialogVisible(false); setSelectedCustomer(null); }}
                                      />
                                      <Button
                                        label="Resend"
                                        icon="pi pi-send"
                                        className="p-button-primary"
                                        loading={resendLoading}
                                        onClick={confirmResend}
                                      />
                                    </div>
                                  }
                                  onHide={() => { setResendDialogVisible(false); setSelectedCustomer(null); }}
                                >
                                  {selectedCustomer && (
                                    <div className="field">
                                      <p className="mb-3">Are you sure you want to resend login credentials to:</p>
                                      <div className="p-3 surface-200 border-round">
                                        <p className="mb-2"><strong>Name:</strong> {selectedCustomer.firstName} {selectedCustomer.lastName}</p>
                                        <p className="mb-2"><strong>Email:</strong> {selectedCustomer.email}</p>
                                        <p className="mb-2"><strong>Phone:</strong> {selectedCustomer.phoneNumber}</p>
                                        <p className="mb-2"><strong>Type:</strong> {toTitleCase(selectedCustomer.customerType)}</p>
                                      </div>
                                      <div className="mt-3 p-3 bg-blue-50 border-round text-sm">
                                        <i className="pi pi-info-circle mr-2 text-blue-500"></i>
                                        A new temporary password will be generated and sent via SMS and Email.
                                      </div>
                                    </div>
                                  )}
                                </Dialog>

                                {/* KRA Import Results Dialog */}
                                <Dialog
                                  header="KRA PIN Import Results"
                                  visible={kraImportDialogVisible}
                                  style={{ width: '700px', maxWidth: '95vw' }}
                                  modal
                                  onHide={() => { setKraImportDialogVisible(false); setKraImportResults(null); }}
                                  footer={
                                    <div className="flex justify-content-end">
                                      <Button
                                        label="Close"
                                        icon="pi pi-times"
                                        className="p-button-text"
                                        onClick={() => { setKraImportDialogVisible(false); setKraImportResults(null); }}
                                        disabled={kraImportLoading}
                                      />
                                    </div>
                                  }
                                >
                                  {kraImportLoading && (
                                    <div className="text-center py-4">
                                      <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
                                      <p className="mt-3 text-muted">Processing Excel file, please wait...</p>
                                    </div>
                                  )}

                                  {!kraImportLoading && kraImportResults && (
                                    <div>
                                      {/* Summary cards */}
                                      <div className="row g-2 mb-3">
                                        {[
                                          { label: 'Total Rows', value: kraImportResults.total, bg: '#e9ecef', color: '#495057' },
                                          { label: 'Updated', value: kraImportResults.updated, bg: '#d4edda', color: '#155724' },
                                          { label: 'Not Found', value: kraImportResults.notFound, bg: '#fff3cd', color: '#856404' },
                                          { label: 'Skipped', value: kraImportResults.skipped, bg: '#e2e3e5', color: '#383d41' },
                                          { label: 'Errors', value: kraImportResults.errors?.length || 0, bg: '#f8d7da', color: '#721c24' },
                                        ].map((card) => (
                                          <div key={card.label} className="col">
                                            <div
                                              style={{
                                                backgroundColor: card.bg,
                                                color: card.color,
                                                borderRadius: '8px',
                                                padding: '10px',
                                                textAlign: 'center',
                                              }}
                                            >
                                              <div style={{ fontSize: '22px', fontWeight: 700 }}>{card.value}</div>
                                              <div style={{ fontSize: '11px', fontWeight: 600 }}>{card.label}</div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>

                                      {/* Detail table */}
                                      {kraImportResults.details && kraImportResults.details.length > 0 && (
                                        <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                                          <table className="table table-sm table-bordered" style={{ fontSize: '13px' }}>
                                            <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
                                              <tr>
                                                <th>Row</th>
                                                <th>Email</th>
                                                <th>KRA PIN</th>
                                                <th>Customer</th>
                                                <th>Status</th>
                                                <th>Reason</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {kraImportResults.details.map((detail, i) => (
                                                <tr key={i}>
                                                  <td>{detail.row}</td>
                                                  <td style={{ wordBreak: 'break-all' }}>{detail.email || '—'}</td>
                                                  <td style={{ fontFamily: 'monospace' }}>{detail.kraPin || '—'}</td>
                                                  <td>{detail.customerName || '—'}</td>
                                                  <td>{getStatusBadge(detail.status)}</td>
                                                  <td style={{ color: '#666' }}>{detail.reason || '—'}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </Dialog>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default CustomerManagement;